import React, { useMemo, useState } from 'react';
import { useData } from '../lib/DataContext.jsx';
import { KpiCard, SectionTitle, ProvenanceFooter, ProvenanceBadge, BandPill, Chip, DataBarCell } from '../components/ui/kits.jsx';
import { Lollipop } from '../components/charts/charts.jsx';
import { fmt, fmtCr, fmtInt, bandColor, CRIT_ORDER, CRIT_COLOR } from '../lib/format.js';
import { ProjectCode } from '../components/ui/ProjectCode.jsx';
import { IndianRupee, TrendingDown, TrendingUp, Zap, HelpCircle, X } from 'lucide-react';

/**
 * CostOfOutcome — the ₹-per-Impact ledger.
 *
 * The single page that translates OECD scores into CFO-language:
 *   "1 point of Sustainability costs HDFC ₹X Cr on average."
 * All calculations use the 67 projects with reported budget values
 * (the funded subset) to keep the ratio honest.
 */
export default function CostOfOutcome() {
  const { data, agg: A } = useData();
  const [showHelp, setShowHelp] = useState(false);

  const ledger = useMemo(() => (data && A ? buildLedger(A) : null), [data, A]);
  if (!data || !A || !ledger) return null;

  const {
    fundedN, totalBudget, avgOverallFunded,
    pointCost, hrdpVsFdp, critCost, partnersRanked,
    bandCost, quadrant, headline,
  } = ledger;

  return (
    <div style={{ padding: 22, display: 'flex', flexDirection: 'column', gap: 18 }} data-testid="page-cost">
      {/* Headline insight banner */}
      <div className="card" style={{
        padding: 18, background: 'linear-gradient(120deg, #042a54, #004c8f)',
        color: '#fff', borderColor: '#042a54', position: 'relative',
      }} data-testid="cost-headline">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 14 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
              <Zap size={16} />
              <div style={{ fontSize: 10.5, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.09em', opacity: .8 }}>
                CFO Signal · Cost of Outcome
              </div>
            </div>
            <div style={{ fontSize: 18, fontWeight: 700, lineHeight: 1.35 }}>
              {headline}
            </div>
            <div style={{ marginTop: 6, fontSize: 11.5, opacity: .75 }}>
              Basis: {fundedN} funded projects · ₹{fmt(totalBudget, 1)} Cr disbursed · programme-average score {fmt(avgOverallFunded, 2)} on the funded cohort.
            </div>
          </div>
          <button
            onClick={() => setShowHelp(true)}
            data-testid="btn-cost-help"
            style={{
              flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: 5,
              background: 'rgba(255,255,255,.12)',
              border: '1px solid rgba(255,255,255,.28)',
              color: '#fff', padding: '6px 12px', borderRadius: 999,
              fontSize: 11.5, fontWeight: 700, cursor: 'pointer',
              backdropFilter: 'blur(4px)',
            }}
          >
            <HelpCircle size={13} /> How is this calculated?
          </button>
        </div>
      </div>

      {/* KPI strip */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14 }}>
        <KpiCard hero testid="kpi-point-cost"
          label="₹ per rubric-point"
          value={`₹${fmt(pointCost.overall, 2)} Cr`}
          sub="Programme benchmark (comparative index)"
        />
        <KpiCard testid="kpi-impact-cost"
          label="Impact · ₹ per rubric-point"
          value={`₹${fmt(pointCost.Impact, 2)} Cr`}
          sub="Comparative, not a unit cost"
        />
        <KpiCard testid="kpi-sustain-cost"
          label="Sustainability · ₹ per rubric-point"
          value={`₹${fmt(pointCost.Sustainability, 2)} Cr`}
          sub="Comparative, not a unit cost"
        />
        <div className="kpi-card fade-up" data-testid="kpi-efficiency-verdict">
          <div className="kpi-label">Capital Efficiency Verdict</div>
          <div style={{ fontSize: 18, fontWeight: 800, marginTop: 4, color: hrdpVsFdp.leader === 'HRDP' ? '#042a54' : '#9b2d20' }}>
            {hrdpVsFdp.leader} wins on {hrdpVsFdp.wins}/{CRIT_ORDER.length}
          </div>
          <div className="kpi-sub">
            {hrdpVsFdp.leader} is {fmt(hrdpVsFdp.multiplier, 2)}× more efficient overall
          </div>
        </div>
      </div>

      {/* Cost per criterion */}
      <div className="card" style={{ padding: 18 }}>
        <SectionTitle right={<ProvenanceBadge kind="live" note={`${fundedN} funded projects only`} />}>
          Cost of a Point · per OECD criterion
        </SectionTitle>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }} className="cost-crit-grid">
          <div>
            <Lollipop
              testid="cost-per-crit"
              rows={CRIT_ORDER.map((c) => ({
                label: c,
                value: 5 - Math.min(critCost[c].costCr / (pointCost.overall * 3), 5), // invert visually: cheaper = longer bar
                color: CRIT_COLOR[c],
                sub: `₹${fmt(critCost[c].costCr, 2)} Cr / point · avg ${fmt(critCost[c].avg, 2)}`,
                valueText: `₹${fmt(critCost[c].costCr, 2)} Cr`,
              }))}
            />
            <div style={{ marginTop: 6, fontSize: 10.5, color: 'var(--ink-4)', fontStyle: 'italic' }}>
              Longer bar = cheaper to move (better capital efficiency).
            </div>
          </div>
          <div>
            <table className="pv-table">
              <thead>
                <tr>
                  <th>Criterion</th>
                  <th style={{ textAlign: 'right' }}>Avg</th>
                  <th style={{ textAlign: 'right' }}>HRDP ₹/pt</th>
                  <th style={{ textAlign: 'right' }}>FDP ₹/pt</th>
                  <th>Cheaper</th>
                </tr>
              </thead>
              <tbody>
                {CRIT_ORDER.map((c) => {
                  const h = hrdpVsFdp.perCrit[c].HRDP;
                  const f = hrdpVsFdp.perCrit[c].FDP;
                  const cheaper = h !== null && f !== null ? (h < f ? 'HRDP' : 'FDP') : '—';
                  const cheaperColor = cheaper === 'HRDP' ? '#042a54' : cheaper === 'FDP' ? '#9b2d20' : 'var(--ink-4)';
                  return (
                    <tr key={c}>
                      <td style={{ color: CRIT_COLOR[c], fontWeight: 700, fontSize: 12 }}>{c}</td>
                      <td className="tabular" style={{ textAlign: 'right', fontSize: 12 }}>{fmt(critCost[c].avg, 2)}</td>
                      <td className="tabular nowrap" style={{ textAlign: 'right', fontSize: 12 }}>
                        {h !== null ? `₹${fmt(h, 2)} Cr` : '—'}
                      </td>
                      <td className="tabular nowrap" style={{ textAlign: 'right', fontSize: 12 }}>
                        {f !== null ? `₹${fmt(f, 2)} Cr` : '—'}
                      </td>
                      <td>
                        <Chip color={cheaperColor}>{cheaper}</Chip>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Band-uplift cost */}
      <div className="card" style={{ padding: 18 }}>
        <SectionTitle right={<ProvenanceBadge kind="live" />}>
          Cost of a Band · what an average project costs at each rung
        </SectionTitle>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
          {['Excellent', 'Good', 'Moderate', 'Poor'].map((b) => {
            const row = bandCost[b];
            if (!row || row.n === 0) return (
              <div key={b} style={{
                padding: 14, background: 'var(--surface-2)', border: '1px solid var(--line)',
                borderRadius: 10, opacity: .55,
              }}>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 6 }}>
                  <BandPill band={b} />
                  <span style={{ fontSize: 10.5, color: 'var(--ink-4)' }}>no funded projects</span>
                </div>
                <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--ink-4)' }} className="tabular">—</div>
              </div>
            );
            return (
              <div key={b} data-testid={`band-cost-${b}`} style={{
                padding: 14, background: 'var(--surface-2)', border: '1px solid var(--line)',
                borderLeft: `3px solid ${bandColor(b)}`, borderRadius: 10,
              }}>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 6 }}>
                  <BandPill band={b} />
                  <span style={{ fontSize: 10.5, color: 'var(--ink-4)' }} className="tabular">n={row.n}</span>
                </div>
                <div style={{ fontSize: 22, fontWeight: 800, color: bandColor(b) }} className="tabular nowrap">
                  ₹{fmt(row.avgBudget, 2)} Cr
                </div>
                <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 3 }}>
                  avg budget · score {fmt(row.avgScore, 2)}
                </div>
                <div style={{ marginTop: 6, fontSize: 10.5, color: 'var(--ink-4)' }} className="tabular">
                  ₹{fmt(row.perPoint, 2)} Cr per score-point
                </div>
              </div>
            );
          })}
        </div>
        <div style={{ marginTop: 10, fontSize: 11.5, color: 'var(--ink-3)' }}>
          Read as — <b>the average cost of one project sitting in each band.</b> Gap between Moderate and Good is the true "uplift cost" line item for board planning.
        </div>
      </div>

      {/* Partner efficiency lollipop */}
      <div className="card" style={{ padding: 18 }}>
        <SectionTitle right={<ProvenanceBadge kind="live" note="Ranked by relative efficiency (median partner = 1.00×)" />}>
          Partner Capital-Efficiency · relative to programme median
        </SectionTitle>
        <div style={{ marginBottom: 10, fontSize: 11.5, color: 'var(--ink-3)' }}>
          The <b>Efficiency Index</b> is unitless — it's this partner's ₹/rubric-point divided by the programme-median ₹/rubric-point.
          Values <b>&lt; 1.00×</b> mean the partner earns each rubric-point at below-median cost (more efficient);
          <b> &gt; 1.00×</b> means above-median cost (less efficient). The raw ₹/point column is shown for context but should not be quoted in isolation — see <i>How is this calculated?</i>
        </div>
        <div className="scroll-y" style={{ maxHeight: 460 }}>
          <table className="pv-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Partner</th>
                <th style={{ textAlign: 'right' }}>Prj</th>
                <th style={{ textAlign: 'right' }}>Budget</th>
                <th style={{ textAlign: 'right' }}>Avg Score</th>
                <th style={{ textAlign: 'right' }}>Efficiency Index</th>
                <th style={{ textAlign: 'right', color: 'var(--ink-4)', fontWeight: 500 }}>₹ / point (raw)</th>
                <th>Relative Efficiency</th>
              </tr>
            </thead>
            <tbody>
              {partnersRanked.map((p, i) => {
                const idx = p.perPoint / ledger.medianPerPoint;
                const idxColor = idx < 0.85 ? '#1f8a4c' : idx > 1.15 ? '#c8452f' : 'var(--ink-2)';
                return (
                  <tr key={p.partner} data-testid={`partner-eff-${i}`}>
                    <td className="tabular" style={{ color: 'var(--ink-4)' }}>{i + 1}</td>
                    <td style={{ fontSize: 12, fontWeight: 600 }}>{p.partner}</td>
                    <td className="tabular" style={{ textAlign: 'right' }}>{p.projects}</td>
                    <td className="tabular nowrap" style={{ textAlign: 'right' }}>{fmtCr(p.budgetCr)}</td>
                    <td className="tabular" style={{ textAlign: 'right', fontWeight: 700, color: bandColor(p.band) }}>{fmt(p.avgScore, 2)}</td>
                    <td className="tabular nowrap" style={{ textAlign: 'right', fontWeight: 800, color: idxColor }}>
                      {fmt(idx, 2)}×
                    </td>
                    <td className="tabular nowrap" style={{ textAlign: 'right', fontSize: 11, color: 'var(--ink-4)' }}>₹{fmt(p.perPoint, 2)} Cr</td>
                    <td>
                      <DataBarCell
                        value={Math.max(0, 5 - Math.min(idx * 2.5, 5))}
                        max={5}
                        colorHint={idx < 1 ? '#1f8a4c' : '#c8452f'}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div style={{ marginTop: 8, fontSize: 11.5, color: 'var(--ink-3)' }}>
          Programme-median cost per rubric-point (partners with ≥2 projects): <b className="tabular">₹{fmt(ledger.medianPerPoint, 2)} Cr</b>.
        </div>
      </div>

      {/* Watch list — expensive AND under-performing */}
      {quadrant.expensiveUnderperformers.length > 0 && (
        <div className="card" style={{
          padding: 18,
          background: 'linear-gradient(120deg, #fbe7e3, #fff 65%)',
          borderColor: '#e23d2833', borderLeft: '4px solid #e23d28',
        }} data-testid="cost-watchlist">
          <SectionTitle right={<Chip color="#c8452f">Board Watch-list</Chip>}>
            High spend · Low outcome — {quadrant.expensiveUnderperformers.length} project{quadrant.expensiveUnderperformers.length === 1 ? '' : 's'} to review
          </SectionTitle>
          <table className="pv-table">
            <thead>
              <tr>
                <th>Project</th>
                <th>Partner</th>
                <th>State</th>
                <th style={{ textAlign: 'right' }}>Budget</th>
                <th style={{ textAlign: 'right' }}>Score</th>
                <th style={{ textAlign: 'right' }}>₹ / point</th>
              </tr>
            </thead>
            <tbody>
              {quadrant.expensiveUnderperformers.map((p, i) => (
                <tr key={p.code} data-testid={`watch-${i}`}>
                  <td style={{ fontWeight: 700 }}><ProjectCode code={p.code} /></td>
                  <td style={{ fontSize: 12 }}>{p.partner || '—'}</td>
                  <td style={{ fontSize: 11.5 }}>{p.state || '—'}</td>
                  <td className="tabular nowrap" style={{ textAlign: 'right' }}>{fmtCr(p.budgetCr)}</td>
                  <td className="tabular" style={{ textAlign: 'right', color: bandColor(p.band), fontWeight: 700 }}>{fmt(p.overall, 2)}</td>
                  <td className="tabular nowrap" style={{ textAlign: 'right', fontWeight: 800, color: '#c8452f' }}>₹{fmt(p.perPoint, 2)} Cr</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ marginTop: 8, fontSize: 11, color: 'var(--ink-3)' }}>
            Rule: budget ≥ median AND ₹/point &gt; 2× median (i.e., paying twice the going rate for what the outcome shows).
          </div>
        </div>
      )}

      <ProvenanceFooter text={`All ratios are computed on the ${fundedN} funded projects with disclosed ₹ values. Zero-budget projects (${A.n - fundedN} of ${A.n}) are excluded from cost ratios but remain in aggregate score analysis. The OECD score is a 0-5 quality rubric — dividing ₹ by it yields a comparative benchmark (useful for ranking types, partners, criteria), not a literal cost-per-outcome. Read "How is this calculated?" for the full methodological note.`} />

      {showHelp && <HowCalculatedModal onClose={() => setShowHelp(false)} fundedN={fundedN} totalBudget={totalBudget} pointCost={pointCost} />}
    </div>
  );
}

// ---------------------------------------------------------------- Modal

function HowCalculatedModal({ onClose, fundedN, totalBudget, pointCost }) {
  return (
    <div
      onClick={onClose}
      data-testid="how-calc-backdrop"
      style={{
        position: 'fixed', inset: 0, zIndex: 100,
        background: 'rgba(4, 42, 84, .55)',
        backdropFilter: 'blur(3px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 20, animation: 'fadeIn .18s ease-out',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        data-testid="how-calc-modal"
        style={{
          background: 'var(--surface)', color: 'var(--ink)',
          borderRadius: 14, maxWidth: 720, width: '100%',
          maxHeight: '85vh', overflow: 'auto',
          boxShadow: '0 20px 60px rgba(0,0,0,.3)',
          border: '1px solid var(--line)',
        }}
      >
        <div style={{
          padding: '16px 22px', borderBottom: '1px solid var(--line)',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          position: 'sticky', top: 0, background: 'var(--surface)', zIndex: 2,
        }}>
          <div>
            <div style={{ fontSize: 10.5, fontWeight: 800, color: 'var(--primary)', textTransform: 'uppercase', letterSpacing: '.09em' }}>
              Methodology
            </div>
            <div style={{ fontSize: 16, fontWeight: 800, marginTop: 2 }}>How is Cost of Outcome calculated?</div>
          </div>
          <button
            onClick={onClose}
            data-testid="how-calc-close"
            style={{
              background: 'transparent', border: '1px solid var(--line)',
              padding: 6, borderRadius: 6, cursor: 'pointer',
              display: 'flex', alignItems: 'center', color: 'var(--ink-3)',
            }}
          ><X size={14} /></button>
        </div>

        <div style={{ padding: 22, display: 'flex', flexDirection: 'column', gap: 16, fontSize: 13, lineHeight: 1.6 }}>

          <Section title="1. What is a “point”?">
            Each project is scored by our independent evaluators against the <b>7 OECD-DAC criteria</b>
            (Relevance · Coherence · Efficiency · Effectiveness · Impact · Sustainability · Branding).
            Every criterion is graded on a <b>0-to-5 scale</b>, and the project's <b>Overall</b> score is the
            weighted average of those 7 criteria — also a value between 0 and 5.
            <div style={{ marginTop: 8, padding: '8px 12px', background: 'var(--surface-2)', borderRadius: 8, fontSize: 12 }}>
              A "<b>point</b>" is one unit on that 0-to-5 quality rubric — <b>not</b> a physical quantity like
              a beneficiary or a hectare. It's a rating scale (closer to Yelp stars than to kilograms).
              The programme's average score today is <b>3.84 / 5</b>.
            </div>
          </Section>

          <Section title="2. Honest caveat — is ₹/point a real unit cost?">
            <div style={{ padding: 12, background: '#e23d2811', border: '1px solid #e23d2833', borderLeft: '3px solid #e23d28', borderRadius: 8, marginBottom: 8 }}>
              <b>Short answer: no.</b> The 0-5 OECD score is a quality index, not a ratio quantity. It has
              no true zero (a "Poor" project still delivered <i>something</i>), and moving 4.5 → 5.0 is much
              harder than moving 3.0 → 3.5. So the formula "₹ ÷ score" is <b>arithmetically valid but
              conceptually a comparative index</b>, not a literal cost-per-outcome.
            </div>
            The absolute rupee quote (<b>₹{fmt(pointCost.overall, 2)} Cr per rubric-point</b>) is a
            <i> common denominator</i> — useful only when you compare like-for-like: HRDP vs FDP,
            partner vs partner, criterion vs criterion. In those comparisons the "arbitrary scale"
            problem cancels out, and the answer to <i>"which side wins per rupee?"</i> is defensible.
            It is <b>not</b> defensible to say "one point of OECD Impact costs HDFC ₹2.25 Cr" as a
            standalone number — the number is a benchmark, not a price tag.
          </Section>

          <Section title="3. The core formula">
            <div style={{ padding: 12, background: '#042a5408', border: '1px solid #042a5422', borderRadius: 8, fontFamily: 'monospace', fontSize: 12.5 }}>
              ₹ per rubric-point &nbsp;=&nbsp; <b>Σ(project budget in ₹ Cr)</b> &nbsp;÷&nbsp; <b>Σ(project score, 0-5 scale)</b>
            </div>
            <div style={{ marginTop: 6, fontSize: 12, color: 'var(--ink-3)' }}>
              We total the budget across the funded cohort and divide by the total scored points across the same cohort.
              This produces the programme's <b>capital-efficiency benchmark</b> —
              currently <b className="tabular">₹{fmt(pointCost.overall, 2)} Cr per rubric-point</b> —
              which every partner / type / criterion below is judged against.
            </div>
          </Section>

          <Section title="4. Per-criterion cost">
            The same formula, but restricted to a single criterion's sub-score:
            <div style={{ padding: 12, background: '#042a5408', border: '1px solid #042a5422', borderRadius: 8, fontFamily: 'monospace', fontSize: 12.5, marginTop: 6 }}>
              ₹ per Impact rubric-point &nbsp;=&nbsp; Σ(budget) &nbsp;÷&nbsp; Σ(Impact sub-score)
            </div>
            <div style={{ marginTop: 6, fontSize: 12, color: 'var(--ink-3)' }}>
              This is why <b>Impact</b> looks the most expensive (₹2.25 Cr/pt) — Impact scores are lower on average (3.30),
              so each rupee "buys" less of it in rubric terms. Efficiency scores are highest (4.44), so ₹/point is lowest.
              <b> Cheaper doesn't mean better</b>; it just means that outcome dimension is easier to move up the rubric
              for HDFC's portfolio.
            </div>
          </Section>

          <Section title="5. HRDP vs FDP verdict">
            For each criterion we compute ₹/point separately for HRDP-typed projects and FDP-typed projects.
            The "Cheaper" column marks which programme type gets more rubric-score for the same rupee.
            The "wins on X/7" verdict is the tally across all 7 criteria. This is a <b>comparative</b>
            claim, which is what the ratio was designed for — the absolute rupee amount does not matter,
            only which side is lower.
          </Section>

          <Section title="6. Cost of a Band">
            For each band (Excellent · Good · Moderate · Poor) we compute the average project budget of the projects that
            currently sit in that band, and the ₹/point of that cohort. Reading the deltas between adjacent bands tells you
            the true "uplift" line-item for board planning.
            <div style={{ marginTop: 8, fontSize: 12, color: 'var(--ink-3)', fontStyle: 'italic' }}>
              Note: this is a <b>correlational</b> observation, not a causal law. A ₹Y-Cr project isn't guaranteed to land in "Good"
              just because Good projects average ₹Y Cr today — and diminishing returns near the top of the rubric mean the
              relationship is definitely non-linear.
            </div>
          </Section>

          <Section title="7. Data caveats">
            <ul style={{ margin: 0, paddingLeft: 18, color: 'var(--ink-3)', fontSize: 12, lineHeight: 1.7 }}>
              <li>Budget values are disclosed for <b>{fundedN} of 186</b> projects. Ratios use only this funded cohort.</li>
              <li>Total disbursement in the ratio: <b className="tabular">₹{fmt(totalBudget, 1)} Cr</b>.</li>
              <li>Scores are from the FY24-25 independent OECD-DAC evaluation, on a 0-5 quality rubric.</li>
              <li>3 criteria (Relevance · Efficiency · Sustainability) are computed live from the 638k survey responses;
                the other 4 are modelled placeholders (see the MODEL badge across the dashboard).
              </li>
              <li>All ₹ values throughout the dashboard are in <b>Crores (₹Cr)</b>. Indian number grouping is used everywhere.</li>
            </ul>
          </Section>

          <div style={{
            padding: '10px 14px', background: '#042a5408',
            border: '1px solid #042a5422', borderRadius: 8, fontSize: 11.5, color: 'var(--ink-3)',
          }}>
            <b style={{ color: 'var(--ink)' }}>TL;DR</b> — every card on this page is a ratio of ₹ (budget)
            over rubric-points (0-to-5 quality units), scoped to the funded cohort. It is a
            <b> comparative benchmark</b> — great for ranking partners, types and criteria against each
            other, <b>not</b> a literal cost-per-outcome. Always read it as "who's above / below the
            programme median?", not "one point costs ₹X."
          </div>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div style={{ fontSize: 12.5, fontWeight: 800, marginBottom: 6, color: 'var(--ink)' }}>{title}</div>
      <div style={{ fontSize: 12.5, color: 'var(--ink-2)' }}>{children}</div>
    </div>
  );
}

// ---------------------------------------------------------------- Ledger builder

function buildLedger(A) {
  // Restrict to funded projects (budget > 0) with scores.
  const funded = A.scatter; // scatter already excludes 0-budget & unscored
  const codes = new Set(funded.map((f) => f.code));
  const fundedFull = A.projectsSorted.filter((p) => codes.has(p.code));

  const fundedN = fundedFull.length;
  const totalBudget = funded.reduce((a, b) => a + b.budgetCr, 0);
  const avgOverallFunded = mean(fundedFull.map((p) => p.overall));

  // Programme-level cost per point of overall
  const pointCost = { overall: totalBudget / (avgOverallFunded * fundedN) };

  // Cost per point of each criterion
  const critCost = {};
  CRIT_ORDER.forEach((c) => {
    const rows = fundedFull.filter((p) => p.criteria && p.criteria[c] != null);
    const budgetSum = rows.reduce((a, b) => a + b.budgetCr, 0);
    const scoreSum = rows.reduce((a, b) => a + b.criteria[c], 0);
    critCost[c] = {
      avg: rows.length ? scoreSum / rows.length : null,
      costCr: rows.length ? budgetSum / scoreSum : null,
    };
    pointCost[c] = critCost[c].costCr;
  });

  // HRDP vs FDP per-criterion cost
  const perCrit = {};
  CRIT_ORDER.forEach((c) => {
    perCrit[c] = { HRDP: null, FDP: null };
    ['HRDP', 'FDP'].forEach((t) => {
      const rows = fundedFull.filter((p) => p.type === t && p.criteria && p.criteria[c] != null);
      const bSum = rows.reduce((a, b) => a + b.budgetCr, 0);
      const sSum = rows.reduce((a, b) => a + b.criteria[c], 0);
      if (rows.length > 0 && sSum > 0) perCrit[c][t] = bSum / sSum;
    });
  });
  const hrdpWins = CRIT_ORDER.filter((c) => perCrit[c].HRDP !== null && perCrit[c].FDP !== null && perCrit[c].HRDP < perCrit[c].FDP).length;
  const fdpWins = CRIT_ORDER.filter((c) => perCrit[c].HRDP !== null && perCrit[c].FDP !== null && perCrit[c].FDP < perCrit[c].HRDP).length;
  const hrdpAvg = avgOf(CRIT_ORDER.map((c) => perCrit[c].HRDP));
  const fdpAvg = avgOf(CRIT_ORDER.map((c) => perCrit[c].FDP));
  const hrdpVsFdp = {
    leader: hrdpWins >= fdpWins ? 'HRDP' : 'FDP',
    wins: Math.max(hrdpWins, fdpWins),
    multiplier: hrdpAvg && fdpAvg ? (Math.max(hrdpAvg, fdpAvg) / Math.min(hrdpAvg, fdpAvg)) : 1,
    perCrit,
  };

  // Band uplift cost
  const bandCost = {};
  ['Excellent', 'Good', 'Moderate', 'Poor'].forEach((b) => {
    const rows = fundedFull.filter((p) => p.band === b);
    const totalB = rows.reduce((a, r) => a + r.budgetCr, 0);
    const totalS = rows.reduce((a, r) => a + r.overall, 0);
    bandCost[b] = {
      n: rows.length,
      avgBudget: rows.length ? totalB / rows.length : null,
      avgScore: rows.length ? totalS / rows.length : null,
      perPoint: rows.length ? totalB / totalS : null,
    };
  });

  // Partner ranking by ₹/point (efficiency)
  const partnersRanked = A.partners
    .filter((p) => p.budgetCr > 0 && p.avgScore !== null && p.projects >= 2)
    .map((p) => ({ ...p, perPoint: p.budgetCr / (p.avgScore * p.projects) }))
    .sort((a, b) => a.perPoint - b.perPoint);
  const medianPerPoint = median(partnersRanked.map((p) => p.perPoint));

  // Watchlist — high spend AND high ₹/point
  const budgets = fundedFull.map((p) => p.budgetCr).sort((a, b) => a - b);
  const medianBudget = budgets[Math.floor(budgets.length / 2)];
  const perPointRows = fundedFull.map((p) => ({ ...p, perPoint: p.budgetCr / p.overall }));
  const medianPP = median(perPointRows.map((p) => p.perPoint));
  const expensiveUnderperformers = perPointRows
    .filter((p) => p.budgetCr >= medianBudget && p.perPoint > 2 * medianPP)
    .sort((a, b) => b.perPoint - a.perPoint)
    .slice(0, 6);

  // Headline
  const headline = buildHeadline({ pointCost, hrdpVsFdp, bandCost });

  return {
    fundedN, totalBudget, avgOverallFunded,
    pointCost, critCost, hrdpVsFdp,
    bandCost, partnersRanked, medianPerPoint,
    quadrant: { expensiveUnderperformers },
    headline,
  };
}

function buildHeadline({ pointCost, hrdpVsFdp, bandCost }) {
  const parts = [];
  if (hrdpVsFdp.multiplier > 1.05) {
    parts.push(`${hrdpVsFdp.leader} projects earn each OECD rubric-point ~${fmt(hrdpVsFdp.multiplier, 2)}× more cheaply than ${hrdpVsFdp.leader === 'HRDP' ? 'FDP' : 'HRDP'} (comparative benchmark, not a literal unit cost).`);
  }
  if (bandCost.Good?.avgBudget && bandCost.Moderate?.avgBudget) {
    const gap = bandCost.Good.avgBudget - bandCost.Moderate.avgBudget;
    if (Math.abs(gap) > 0.05) {
      parts.push(gap > 0
        ? `The average Good-band project costs ₹${fmt(gap, 2)} Cr more than the average Moderate-band project.`
        : `Counter-intuitively, the average Good-band project costs ₹${fmt(-gap, 2)} Cr less than the average Moderate-band project.`);
    }
  }
  parts.push(`Programme benchmark: ₹${fmt(pointCost.overall, 2)} Cr per rubric-point on the 0-5 OECD quality scale.`);
  return parts.slice(0, 2).join(' ');
}

// ---------------------------------------------------------------- helpers

function mean(arr) {
  const clean = arr.filter((v) => v !== null && v !== undefined && !Number.isNaN(v));
  if (clean.length === 0) return null;
  return clean.reduce((a, b) => a + b, 0) / clean.length;
}
function median(arr) {
  const clean = arr.filter((v) => v !== null && v !== undefined && !Number.isNaN(v)).sort((a, b) => a - b);
  if (clean.length === 0) return null;
  const mid = Math.floor(clean.length / 2);
  return clean.length % 2 ? clean[mid] : (clean[mid - 1] + clean[mid]) / 2;
}
function avgOf(arr) {
  const clean = arr.filter((v) => v !== null && v !== undefined && !Number.isNaN(v));
  return clean.length ? clean.reduce((a, b) => a + b, 0) / clean.length : null;
}
